/**
 * Session persistence hook for the creative evaluation tool.
 *
 * Encapsulates all sessionStorage read/write logic so the page component
 * stays a thin orchestrator.  Two stores are used to avoid re-serializing
 * the full image batch on every eval-state keystroke:
 *
 *   1. Base store  — written when the loaded image batch changes.
 *   2. Draft store — written on eval state changes (debounced 150 ms).
 *
 * On mount the hook restores both stores, validates brand consistency, and
 * returns the merged data so the caller can seed its React Query cache and
 * local state without a redundant API call.
 *
 * Session is cleared on unmount (route exit) and on login (see login.tsx).
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { InfiniteData } from "@tanstack/react-query";
import type {
  EvalImage,
  EvalQueryParams,
  EvalResponse,
  FilterValues,
  ImageEvalState,
  ReviewSession,
  SerializedImageEvalState
} from "../types/creative-eval";
import {
  clearReviewSession,
  deserializeEvalStates,
  loadReviewSession,
  saveReviewSessionBase,
  saveReviewSessionDraft,
  serializeEvalStates,
  hasEvaluationSelections
} from "../components/creative-eval/helpers";
import { DEBUG_QUERY_INITIAL } from "../components/creative-eval/constants";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionRestoreResult {
  /** Restored filters, or null when no valid session exists. */
  filters: FilterValues | null;
  /** Deserialized eval states from the draft store. */
  evalStates: Record<number, ImageEvalState>;
  /** Query params rebuilt from the restored filters. */
  queryParams: EvalQueryParams | null;
  /** Whether eval-in-progress work existed at restore time. */
  hasUnsavedChanges: boolean;
  /** Pre-built initialData for useInfiniteQuery so we skip the first fetch. */
  initialQueryData: InfiniteData<EvalResponse, string | null> | undefined;
  /** Debug query string from last session. */
  debugQuery: string;
}

interface PersistBaseArgs {
  filters: FilterValues;
  queryParams: EvalQueryParams;
  images: EvalImage[];
  nextPageToken: string | null;
  debugQuery: string;
}

interface PersistDraftArgs {
  evalStates: Record<number, ImageEvalState>;
  hasUnsavedChanges: boolean;
}

export interface UseCreativeEvalSessionReturn {
  restored: SessionRestoreResult;
  /** Write the base store (images, filters, query params). */
  persistBase: (args: PersistBaseArgs) => void;
  /** Write the draft store (eval states, unsaved flag) — debounced 150 ms. */
  persistDraft: (args: PersistDraftArgs) => void;
  /** Explicitly clear both stores (called on "Apply Filters"). */
  clearSession: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildInitialQueryData(
  session: ReviewSession,
  queryParams: EvalQueryParams | null
): InfiniteData<EvalResponse, string | null> | undefined {
  if (!queryParams || JSON.stringify(queryParams) !== JSON.stringify(session.queryParams)) return undefined;

  return {
    pages: [
      {
        count: session.images.length,
        images: session.images,
        next_page_token: session.nextPageToken,
        debug_query: session.debugQuery
      }
    ],
    pageParams: [null]
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCreativeEvalSession(): UseCreativeEvalSessionReturn {
  // ---- Restore on mount (runs once) ----
  const session = useMemo(() => loadReviewSession(), []);

  const restoredEvalStates = useMemo(() => deserializeEvalStates(session?.evalStates), [session]);

  const restoredQueryParams = session?.queryParams ?? null;

  const initialQueryData = useMemo(
    () => (session ? buildInitialQueryData(session, restoredQueryParams) : undefined),
    [session, restoredQueryParams]
  );

  const restored: SessionRestoreResult = useMemo(
    () => ({
      filters: session?.filters ?? null,
      evalStates: restoredEvalStates,
      queryParams: restoredQueryParams,
      hasUnsavedChanges: Boolean(session?.hasUnsavedChanges && hasEvaluationSelections(restoredEvalStates)),
      initialQueryData,
      debugQuery: session?.debugQuery || DEBUG_QUERY_INITIAL
    }),
    [session, restoredEvalStates, restoredQueryParams, initialQueryData]
  );

  // ---- Persist helpers ----
  const persistBase = useCallback((args: PersistBaseArgs) => {
    saveReviewSessionBase(args);
  }, []);

  const draftTimerRef = useRef<number | null>(null);

  const persistDraft = useCallback((args: PersistDraftArgs) => {
    if (draftTimerRef.current !== null) window.clearTimeout(draftTimerRef.current);

    draftTimerRef.current = window.setTimeout(() => {
      draftTimerRef.current = null;
      saveReviewSessionDraft({
        evalStates: serializeEvalStates(args.evalStates) as Record<string, SerializedImageEvalState>,
        hasUnsavedChanges: args.hasUnsavedChanges
      });
    }, 150);
  }, []);

  // ---- Cleanup on unmount (route exit) ----
  useEffect(() => {
    return () => {
      if (draftTimerRef.current !== null) window.clearTimeout(draftTimerRef.current);
      clearReviewSession();
    };
  }, []);

  // ---- Beforeunload guard (delegates to parent's hasUnsavedChanges) ----
  // Intentionally NOT handled here — the parent component owns
  // hasUnsavedChanges state which changes over time; the effect lives
  // in the page component where the dependency is natural.

  return { restored, persistBase, persistDraft, clearSession: clearReviewSession };
}
